if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
      }

      hide() {
        if (this._resetActiveZoom) this._resetActiveZoom(false);
        super.hide();
        document.body.classList.remove('media-modal-open');
        document.body.style.top = '';
        if (typeof this.scrollPosition === 'number') {
          window.scrollTo(0, this.scrollPosition);
        }
      }

      show(opener) {
        this.scrollPosition = window.scrollY;
        super.show(opener);
        this.showActiveMedia();
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.classList.add('media-modal-open');
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        // On mobile the lightbox is a JS-driven, one-image-per-swipe carousel.
        // On larger screens the media stack scrolls vertically.
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        if (isMobile) {
          this.initMobileSwiper();
          const slides = Array.from(this.swiperTrack.children);
          const index = slides.indexOf(activeMedia);
          this.goToSlide(index < 0 ? 0 : index, false);
        } else {
          activeMedia.scrollIntoView({ behavior: 'auto' });
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      goToSlide(index, animate = true) {
        if (!this.swiperTrack) return;
        if (this._resetActiveZoom) this._resetActiveZoom(false);
        const total = this.swiperTrack.children.length;
        this.currentIndex = Math.max(0, Math.min(index, total - 1));
        if (!animate) this.swiperTrack.style.transition = 'none';
        this.swiperTrack.style.transform = `translateX(-${this.currentIndex * 100}vw)`;
        if (!animate) {
          // Force a reflow, then restore the CSS transition for later swipes.
          void this.swiperTrack.offsetWidth;
          this.swiperTrack.style.transition = '';
        }
        const newImg = this.swiperTrack.children[this.currentIndex];
        if (newImg) {
          newImg.style.transition = 'none';
          newImg.style.transform = 'none';
        }
      }

      initMobileSwiper() {
        this.swiperTrack = this.querySelector('[role="document"]');
        if (this.swiperInitialized) return;
        this.swiperInitialized = true;
        this.currentIndex = 0;
        this._mediaZoomed = false;

        const MAX_SCALE = 4;
        const pointers = new Map();
        let mode = null; // 'swipe' | 'pan' | 'pinch'
        let scale = 1;
        let tx = 0;
        let ty = 0;
        let startDist = 0;
        let startScale = 1;
        let lastX = 0;
        let lastY = 0;
        let swipeStartX = 0;
        let swipeStartY = 0;
        let swipeIsHorizontal = null;

        const activeImg = () => this.swiperTrack.children[this.currentIndex];

        const apply = (animate) => {
          const img = activeImg();
          if (!img) return;
          img.style.transition = animate ? 'transform 0.25s ease' : 'none';
          img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
        };

        const clampPan = () => {
          const maxX = Math.max(0, ((scale - 1) * window.innerWidth) / 2);
          const maxY = Math.max(0, ((scale - 1) * window.innerHeight) / 2);
          tx = Math.min(maxX, Math.max(-maxX, tx));
          ty = Math.min(maxY, Math.max(-maxY, ty));
        };

        const resetZoom = (animate) => {
          scale = 1;
          tx = 0;
          ty = 0;
          this._mediaZoomed = false;
          apply(animate);
        };
        this._resetActiveZoom = resetZoom;

        this.swiperTrack.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse') return;
          pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          if (pointers.size === 2) {
            const pts = [...pointers.values()];
            startDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
            startScale = scale;
            mode = 'pinch';
          } else if (pointers.size === 1) {
            swipeStartX = lastX = event.clientX;
            swipeStartY = lastY = event.clientY;
            swipeIsHorizontal = null;
            mode = scale > 1.02 ? 'pan' : 'swipe';
          }
        });

        this.swiperTrack.addEventListener('pointermove', (event) => {
          if (!pointers.has(event.pointerId)) return;
          pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

          if (mode === 'pinch' && pointers.size >= 2) {
            const pts = [...pointers.values()];
            const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
            if (startDist > 0) {
              scale = Math.min(MAX_SCALE, Math.max(1, startScale * (dist / startDist)));
              this._mediaZoomed = scale > 1.02;
              clampPan();
              apply(false);
            }
          } else if (mode === 'pan' && pointers.size === 1) {
            tx += event.clientX - lastX;
            ty += event.clientY - lastY;
            lastX = event.clientX;
            lastY = event.clientY;
            clampPan();
            apply(false);
          } else if (mode === 'swipe' && pointers.size === 1) {
            const dx = event.clientX - swipeStartX;
            const dy = event.clientY - swipeStartY;
            if (swipeIsHorizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
              swipeIsHorizontal = Math.abs(dx) > Math.abs(dy);
            }
          }
        });

        const onUp = (event) => {
          if (!pointers.has(event.pointerId)) return;
          const endedMode = mode;
          const upX = event.clientX;
          pointers.delete(event.pointerId);

          if (endedMode === 'swipe' && pointers.size === 0 && scale <= 1.02) {
            const dx = upX - swipeStartX;
            if (swipeIsHorizontal && Math.abs(dx) > 40) {
              this.goToSlide(this.currentIndex + (dx < 0 ? 1 : -1), true);
            }
          }

          if (endedMode === 'pinch' && pointers.size < 2) {
            if (scale <= 1.05) {
              resetZoom(true);
            } else {
              clampPan();
              apply(true);
            }
          }

          if (pointers.size === 1) {
            // A finger remains after a pinch: continue panning/swiping from it.
            const remaining = [...pointers.values()][0];
            lastX = swipeStartX = remaining.x;
            lastY = swipeStartY = remaining.y;
            swipeIsHorizontal = null;
            mode = scale > 1.02 ? 'pan' : 'swipe';
          } else if (pointers.size === 0) {
            mode = null;
          }
        };

        this.swiperTrack.addEventListener('pointerup', onUp);
        this.swiperTrack.addEventListener('pointercancel', onUp);
      }
    }
  );
}
